import { Component, computed, effect, inject, signal, viewChild } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { UserStore } from '../../../users/services/user-store';
import { UserCommandService } from './services/user-command.service';
import { ElciaButton } from '../../../../components/buttons/button/button';
import { ElciaDialog } from '../../../../components/dialog/dialog';
import { ConfirmDialog } from '../../../../components/confirm-dialog/confirm-dialog';
import { ConfirmService } from '../../../../services/confirm.service';
import { CurrentUserService } from '../../../../services/current-user.service';
import { UserSelect } from '../../../../../api';
import { UserGrid } from './components/user-grid/user-grid';
import { UserForm } from './components/user-form/user-form';
import { UserFormValue } from './models/user-form-value';

@Component({
  selector: 'elcia-user-settings',
  imports: [TranslatePipe, ElciaButton, ElciaDialog, ConfirmDialog, UserGrid, UserForm],
  providers: [UserCommandService],
  templateUrl: './user-settings.html',
  styleUrl: './user-settings.scss',
})
export class UserSettings {
  private readonly userStore = inject(UserStore);
  private readonly commandService = inject(UserCommandService);
  private readonly confirmService = inject(ConfirmService);
  private readonly currentUserService = inject(CurrentUserService);

  private readonly userForm = viewChild<UserForm>(UserForm);

  protected readonly users = computed(() => this.userStore.users());
  protected readonly isLoading = computed(() => this.userStore.isDataLoading());
  protected readonly currentUserId = computed(() => this.currentUserService.currentUser()?.id ?? null);

  protected readonly isFormDisplayed = signal(false);
  protected readonly isSaving = computed(() => this.commandService.createMutation.isPending());

  protected showForm(): void {
    this.isFormDisplayed.set(true);
  }

  protected closeForm(): void {
    this.isFormDisplayed.set(false);
  }

  protected onDialogSave(): void {
    this.userForm()?.submit();
  }

  protected onFormSubmit(value: UserFormValue): void {
    this.commandService.createMutation.mutate({
      email: value.email!,
      password: value.password!,
      firstName: value.firstName,
      lastName: value.lastName,
      legalEntityId: value.legalEntityId!,
      localeIsoCode: value.localeIsoCode!,
    });
  }

  protected deleteUser(user: UserSelect): void {
    this.confirmService.confirm({
      title: 'entity.user',
      message: `${user.fullName} (${user.email})`,
      accept: () => {
        this.commandService.deleteMutation.mutate(user.id);
      },
    });
  }

  private readonly closeFormOnSuccess = effect(() => {
    if (this.commandService.createMutation.isSuccess()) {
      this.closeForm();
    }
  });
}
